import { Ray } from 'playcanvas';

import { raycastSceneMeshes } from '../collision';
import { ElementType } from '../element';
import { Events } from '../events';
import { Scene } from '../scene';

const annotationTypes = new Set(['pin', 'evidence', 'witness', 'vehicle', 'poi', 'damage']);

type AnnotationType = 'pin' | 'evidence' | 'witness' | 'vehicle' | 'poi' | 'damage';

class AnnotationTool {
    activate: () => void;
    deactivate: () => void;

    constructor(events: Events, scene: Scene, parent: HTMLElement, canvasContainer: { dom: HTMLElement }) {
        const editor = document.createElement('div');
        editor.id = 'annotation-tool-editor';
        editor.className = 'select-toolbar pcui-hidden';
        editor.style.display = 'none';
        editor.style.bottom = 'auto';
        editor.style.transform = 'translate(-50%, -100%)';
        editor.style.gap = '8px';
        editor.style.padding = '8px';
        editor.style.height = 'auto';
        editor.style.zIndex = '20';
        canvasContainer.dom.appendChild(editor);

        editor.addEventListener('pointerdown', (event) => {
            event.stopPropagation();
        });

        const typeSelect = document.createElement('select');
        typeSelect.id = 'annotation-tool-type';
        typeSelect.className = 'pcui-input';
        ['pin', 'evidence', 'witness', 'vehicle', 'poi', 'damage'].forEach((type) => {
            const option = document.createElement('option');
            option.value = type;
            option.textContent = type;
            typeSelect.appendChild(option);
        });

        const labelInput = document.createElement('input');
        labelInput.id = 'annotation-tool-label';
        labelInput.className = 'pcui-input';
        labelInput.type = 'text';
        labelInput.placeholder = 'Annotation label';
        labelInput.maxLength = 256;

        const confirmButton = document.createElement('button');
        confirmButton.id = 'annotation-tool-confirm';
        confirmButton.className = 'pcui-button select-toolbar-button';
        confirmButton.textContent = 'Confirm';

        const cancelButton = document.createElement('button');
        cancelButton.id = 'annotation-tool-cancel';
        cancelButton.className = 'pcui-button select-toolbar-button';
        cancelButton.textContent = 'Cancel';

        editor.appendChild(typeSelect);
        editor.appendChild(labelInput);
        editor.appendChild(confirmButton);
        editor.appendChild(cancelButton);

        let active = false;
        let clicked = false;
        let pendingPosition: [number, number, number] | null = null;

        const isPrimary = (event: PointerEvent) => {
            return event.pointerType === 'mouse' ? event.button === 0 : event.isPrimary;
        };

        const updateConfirmEnabled = () => {
            confirmButton.disabled = !pendingPosition || labelInput.value.trim().length === 0;
        };

        const hideEditor = () => {
            pendingPosition = null;
            labelInput.value = '';
            typeSelect.value = 'pin';
            editor.classList.add('pcui-hidden');
            editor.style.display = 'none';
            updateConfirmEnabled();
        };

        const showEditor = (x: number, y: number, position: [number, number, number]) => {
            pendingPosition = position;
            editor.style.left = `${x}px`;
            editor.style.top = `${y}px`;
            editor.classList.remove('pcui-hidden');
            editor.style.display = 'flex';
            labelInput.focus();
            updateConfirmEnabled();
        };

        const confirmPlacement = () => {
            if (!active || !pendingPosition) {
                return;
            }

            const label = labelInput.value.trim();
            if (!label) {
                updateConfirmEnabled();
                return;
            }

            const typeValue = typeSelect.value as AnnotationType;
            if (!annotationTypes.has(typeValue)) {
                return;
            }

            const id = (typeof crypto !== 'undefined' && 'randomUUID' in crypto) ?
                crypto.randomUUID() :
                `anno-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

            events.fire('annotations.add', {
                id,
                worldPosition: pendingPosition,
                type: typeValue,
                label,
                author: 'editor',
                createdAt: new Date().toISOString()
            });

            hideEditor();
        };

        const cancelPlacement = () => {
            hideEditor();
        };

        const pointerdown = (event: PointerEvent) => {
            if (!active || !isPrimary(event)) {
                return;
            }
            clicked = true;
        };

        const pointermove = () => {
            clicked = false;
        };

        const pickMeshPosition = (event: PointerEvent) => {
            const selection = events.invoke('selection') as { type?: string } | null;
            if (selection?.type !== ElementType.model) {
                return null;
            }

            const rect = scene.canvas.getBoundingClientRect();
            const screenX = event.clientX - rect.left;
            const screenY = event.clientY - rect.top;
            const ray = new Ray();
            scene.camera.getRay(screenX, screenY, ray);

            const hit = raycastSceneMeshes(scene, ray);
            if (!hit || hit.mesh !== selection) {
                return null;
            }

            return [hit.position.x, hit.position.y, hit.position.z] as [number, number, number];
        };

        const pointerup = async (event: PointerEvent) => {
            if (!active || !clicked || !isPrimary(event)) {
                return;
            }
            clicked = false;

            let pickedPosition = pickMeshPosition(event);

            if (!pickedPosition) {
                const result = await scene.camera.intersect(
                    event.offsetX / canvasContainer.dom.clientWidth,
                    event.offsetY / canvasContainer.dom.clientHeight
                );

                if (!result) {
                    return;
                }

                pickedPosition = [result.position.x, result.position.y, result.position.z];
            }

            showEditor(event.offsetX, event.offsetY, pickedPosition);
            event.preventDefault();
            event.stopPropagation();
        };

        const keydown = (event: KeyboardEvent) => {
            if (!active) {
                return;
            }

            if (event.key === 'Escape' && !editor.classList.contains('pcui-hidden')) {
                cancelPlacement();
                event.preventDefault();
                event.stopPropagation();
            } else if (event.key === 'Enter' && !editor.classList.contains('pcui-hidden')) {
                confirmPlacement();
                event.preventDefault();
                event.stopPropagation();
            }
        };

        labelInput.addEventListener('input', updateConfirmEnabled);
        typeSelect.addEventListener('change', updateConfirmEnabled);
        confirmButton.addEventListener('click', confirmPlacement);
        cancelButton.addEventListener('click', cancelPlacement);

        this.activate = () => {
            active = true;
            parent.style.display = 'block';
            canvasContainer.dom.style.cursor = 'crosshair';
            canvasContainer.dom.addEventListener('pointerdown', pointerdown);
            canvasContainer.dom.addEventListener('pointermove', pointermove);
            canvasContainer.dom.addEventListener('pointerup', pointerup, true);
            document.addEventListener('keydown', keydown, true);
            hideEditor();
        };

        this.deactivate = () => {
            active = false;
            clicked = false;
            canvasContainer.dom.style.cursor = '';
            canvasContainer.dom.removeEventListener('pointerdown', pointerdown);
            canvasContainer.dom.removeEventListener('pointermove', pointermove);
            canvasContainer.dom.removeEventListener('pointerup', pointerup, true);
            document.removeEventListener('keydown', keydown, true);
            hideEditor();
        };
    }
}

export { AnnotationTool };
